import moment, { Moment } from "moment";
import { Task, TaskLink } from "../api/Tasks";
import { dateFormat } from "./dates";

// Span is the stretch of days a task's bar covers.
export type Span = {
    task: Task;
    start: Moment;
    end: Moment;
}

// placeTasks lays a set of tasks out. A task with a due date sits on that date and
// runs back to whatever it waits on; a task without one follows its dependencies
// instead, so a chain of undated work still reads in the order it has to happen. A
// task with neither a date nor anything to follow is handed back as unplaced, unless
// fill is set, in which case its neighbours in the chain say where it goes.
export function placeTasks(tasks: Task[], fill: boolean) {
    const byId = new Map(tasks.map(task => [task.id, task]));
    const placed = new Map<number, Span | null>();
    const walking = new Set<number>();

    // linkEnd is the day the task at the other end of an edge finishes on, which is
    // what anything waiting on it starts from. A task outside the set isn't laid out
    // here, so it only offers the due date its link carries.
    const linkEnd = (link: TaskLink) => {
        const task = byId.get(link.id);
        if (!task) return link.dueDate ? moment(link.dueDate, dateFormat) : null;

        const span = place(task);
        return span ? span.end : null;
    };

    function place(task: Task): Span | null {
        const already = placed.get(task.id);
        if (already !== undefined) return already;

        // the server rejects circular dependencies, so this only stops a bad graph
        // from running the walk forever
        if (walking.has(task.id)) return null;
        walking.add(task.id);

        // tracked as a timestamp so the comparison doesn't need a null check on
        // every pass
        let latest = 0;
        for (const link of task.blockedBy) {
            const end = linkEnd(link);
            if (end && end.valueOf() > latest) latest = end.valueOf();
        }

        walking.delete(task.id);

        const after = latest > 0 ? moment(latest).add(1, "day") : null;
        let span: Span | null = null;

        if (task.dueDate) {
            const end = moment(task.dueDate, dateFormat);
            span = {
                task: task,
                start: after && after.isBefore(end) ? after : end.clone(),
                end: end,
            };
        } else if (after) {
            // nothing says how long it takes, so it takes the day after its last
            // blocker
            span = { task: task, start: after, end: after.clone() };
        }

        placed.set(task.id, span);
        return span;
    }

    const spanOf = new Map<number, Span>();
    tasks.forEach(task => {
        const span = place(task);
        if (span) spanOf.set(task.id, span);
    });

    if (fill) fillGaps(tasks, byId, spanOf);

    const spans: Span[] = [];
    const unplaced: Task[] = [];

    tasks.forEach(task => {
        const span = spanOf.get(task.id);
        if (span) spans.push(span);
        else unplaced.push(task);
    });

    // the rows read down the chart in the order the work happens, rather than in the
    // order the list gave them
    spans.sort((a, b) => a.start.valueOf() - b.start.valueOf()
        || a.end.valueOf() - b.end.valueOf()
        || a.task.id - b.task.id);

    return { spans: spans, unplaced: unplaced };
}

// fillGaps puts the tasks no date reaches somewhere anyway. A task sits after
// whatever it waits on and before whatever waits on it, so a position spreads out
// from any task that has one; a chain carrying no date at all is anchored on today,
// at the end nothing in it waits on, and runs forward from there.
function fillGaps(tasks: Task[], byId: Map<number, Task>, spanOf: Map<number, Span>) {
    // a task placed on one pass is what the next one along the chain sits against, so
    // this runs until a pass has nothing left to place
    const spread = () => {
        let moved = true;

        while (moved) {
            moved = false;

            tasks.forEach(task => {
                if (spanOf.has(task.id)) return;

                const day = beside(task, spanOf);
                if (!day) return;

                spanOf.set(task.id, { task: task, start: day, end: day.clone() });
                moved = true;
            });
        }
    };

    spread();

    // whatever is left is a chain no date reaches at all, so it's anchored on today
    // and spread out from there. Each pass anchors one more chain.
    for (let anchor = chainStart(tasks, byId, spanOf); anchor; anchor = chainStart(tasks, byId, spanOf)) {
        const today = moment().startOf("day");
        spanOf.set(anchor.id, { task: anchor, start: today, end: today.clone() });
        spread();
    }
}

// beside is the day a task sits on when only its neighbours say where it goes: the
// day after the last of the tasks it waits on, or failing that the day before the
// first of the tasks waiting on it.
function beside(task: Task, spanOf: Map<number, Span>) {
    // tracked as timestamps so the comparisons don't need a null check on every pass
    let after = 0;
    task.blockedBy.forEach(link => {
        const span = spanOf.get(link.id);
        if (span && span.end.valueOf() > after) after = span.end.valueOf();
    });

    if (after > 0) return moment(after).add(1, "day");

    let before = 0;
    task.followedBy.forEach(link => {
        const span = spanOf.get(link.id);
        if (span && (before === 0 || span.start.valueOf() < before)) before = span.start.valueOf();
    });

    if (before > 0) return moment(before).subtract(1, "day");
    return null;
}

// chainStart picks the task the next undated chain is anchored on: the one nothing
// else in the set waits for, so the chain runs forward from the anchor rather than
// back into days that have gone by.
function chainStart(tasks: Task[], byId: Map<number, Task>, spanOf: Map<number, Span>) {
    const left = tasks.filter(task => !spanOf.has(task.id));
    if (left.length === 0) return null;

    return left.find(task => !task.blockedBy.some(link => byId.has(link.id))) || left[0];
}

// chartRange covers every span with a day of air either side, so the first and last
// bars don't sit against the edge.
export function chartRange(spans: Span[]) {
    return {
        start: moment.min(spans.map(span => span.start)).clone().subtract(1, "day"),
        end: moment.max(spans.map(span => span.end)).clone().add(1, "day"),
    };
}

// dayOffset is how many days into the chart a date sits, which is what turns a date
// into an x position.
export function dayOffset(from: Moment, date: Moment) {
    return date.diff(from, "days");
}
